import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiService } from '../api/api.service';
import { PaymentPlan, Property, PropertyFilter } from '../models/property.model';
import { Project } from '../models/project.model';
import { API_CONFIG } from '../config/api.config';
import { map, switchMap } from 'rxjs/operators';
import { stratusFileProxyUrl } from '../../shared/utils/stratus-url.util';
import { propertyStatusKey } from '../../shared/utils/property-status.util';

export interface PropertyListResult {
    items: Property[];
    total: number;
    page: number;
    perPage: number;
    projects?: Project[];
}

@Injectable({
    providedIn: 'root'
})
export class PropertiesService {
    private http = inject(HttpClient);
    private api = inject(ApiService); // Keep for authenticated writes if needed
    private readonly PUBLIC_FEED_PATH = `${API_CONFIG.PUBLIC_URL}/properties/feed`;
    private readonly PUBLIC_DETAIL_PATH = `${API_CONFIG.PUBLIC_URL}/properties`;
    private readonly AUTH_PATH = '/API2/properties';

    getProperties(
        filter: PropertyFilter = {},
        options: { page?: number; perPage?: number; includeProjects?: boolean } = {}
    ): Observable<PropertyListResult> {
        const perPage = options.perPage ?? 10;
        const page = options.page ?? 1;
        const baseParams: Record<string, string> = {
            per_page: String(perPage),
            page: String(page),
        };

        const allowedFilters: Array<keyof PropertyFilter> = [
            'type',
            'bedrooms',
            'bathrooms',
            'status',
            'country',
            'location',
            'project_id',
            'search',
            'lat',
            'lng',
            'radius_km',
            'radius',
        ];

        allowedFilters.forEach((key) => {
            const value = filter[key];
            if (value === undefined || value === null || value === '') return;
            if (key === 'project_id') {
                baseParams['project_id'] = value.toString();
                return;
            }
            baseParams[key] = value.toString();
        });

        if (options.includeProjects) {
            baseParams['include'] = 'projects';
        }

        const params = new HttpParams({ fromObject: baseParams });

        return this.fetchPublic<any>(this.PUBLIC_FEED_PATH, params).pipe(
            map((response) => {
                const items = Array.isArray(response?.items) ? response.items : [];
                const meta = response?.pagination || {};
                const projectsRaw = Array.isArray(response?.projects) ? response.projects : null;
                const projects: Project[] | undefined = projectsRaw
                    ? projectsRaw.map((p: any): Project => ({
                        id: String(p?.id || ''),
                        name: String(p?.name || ''),
                        images: []
                    }))
                    : undefined;
                const total = Number(meta.total ?? items.length);
                const safeTotal = Number.isFinite(total) && total >= 0 ? total : items.length;
                const p = Number(meta.page ?? page);
                const pp = Number(meta.perPage ?? meta.per_page ?? perPage);
                return {
                    items: items.map((item: any) => this.mapPropertyVNext(item)),
                    total: safeTotal,
                    page: Number.isFinite(p) && p > 0 ? p : page,
                    perPage: Number.isFinite(pp) && pp > 0 ? pp : perPage,
                    projects
                };
            })
        );
    }

    getPropertiesAuth(
        filter: PropertyFilter = {},
        options: { pendingApproval?: boolean; includeDeleted?: boolean; page?: number; perPage?: number } = {}
    ): Observable<PropertyListResult> {
        const perPage = options.perPage ?? 10;
        const page = options.page ?? 1;
        const baseParams: Record<string, string> = {
            per_page: String(perPage),
            page: String(page),
            sort_by: 'MODIFIEDTIME',
            sort_order: 'desc',
        };

        const allowedFilters: Array<keyof PropertyFilter> = [
            'type',
            'bedrooms',
            'bathrooms',
            'status',
            'country',
            'location',
            'project_id',
            'search',
            'isApproved',
            'isDeleted'
        ];

        allowedFilters.forEach((key) => {
            const value = filter[key];
            if (value === undefined || value === null || value === '') return;
            if (key === 'project_id') {
                baseParams['project_id'] = value.toString();
                return;
            }
            if (key === 'isApproved') {
                baseParams['is_approved'] = value.toString();
                return;
            }
            if (key === 'isDeleted') {
                baseParams['is_deleted'] = value.toString();
                return;
            }
            baseParams[key] = value.toString();
        });

        if (options.pendingApproval) {
            baseParams['pending_approval'] = 'true';
        }
        if (options.includeDeleted) {
            baseParams['include_deleted'] = 'true';
        }

        const params = new HttpParams({ fromObject: baseParams });

        return this.api.get<any>(this.AUTH_PATH, params).pipe(
            map((response) => this.normalizeListResponse(response, page, perPage)),
            map(({ items, meta }) => ({
                items: items.map((item: any) => this.mapProperty(item)),
                total: meta.total,
                page: meta.page,
                perPage: meta.perPage
            }))
        );
    }

    getProperty(id: string): Observable<Property> {
        const params = new HttpParams();
        return this.fetchPublic<any>(`${this.PUBLIC_DETAIL_PATH}/${id}`, params).pipe(
            map(response => this.mapPropertyVNext(response))
        );
    }

    // ---------------------------------------------
    // Bulk unit import (API2)
    // ---------------------------------------------

    bulkImportPreview(
        fileId: string,
        payload: {
            project_id: string;
            currency_code?: string | null;
            country_code?: string | null;
            create_leads_for_reserved?: boolean;
        }
    ): Observable<any> {
        return this.api.post<any>(`/API2/properties/bulk-import/preview`, {
            ...payload,
            file_id: String(fileId || '').trim()
        });
    }

    bulkImportApply(
        fileId: string,
        payload: {
            project_id: string;
            currency_code?: string | null;
            country_code?: string | null;
            create_leads_for_reserved?: boolean;
        }
    ): Observable<any> {
        return this.api.post<any>(`/API2/properties/bulk-import/apply`, {
            ...payload,
            file_id: String(fileId || '').trim()
        });
    }

    getPropertyAuth(id: string): Observable<Property> {
        return this.api.get<any>(`${this.AUTH_PATH}/${id}`).pipe(
            map((response) => {
                const data = response?.data ?? response ?? {};
                const propertyRaw = data?.property ?? data?.data?.property ?? data?.data ?? data;
                return this.mapProperty(propertyRaw);
            })
        );
    }

    getPropertyEditPayload(id: string): Observable<{
        property: Property;
        projects: any[];
        phases: any[];
        categories: any[];
        features: any[];
        currencies: any[];
    }> {
        return this.api.get<any>(`${this.AUTH_PATH}/${id}/edit-payload`).pipe(
            map((response) => {
                const data = response?.data || response || {};
                const propertyRaw = data?.property || data?.data || data?.propertyRow;
                return {
                    property: this.mapProperty(propertyRaw || data),
                    projects: data?.projects || [],
                    phases: data?.phases || [],
                    categories: data?.categories || [],
                    features: data?.features || [],
                    currencies: data?.currencies || []
                };
            })
        );
    }

    savePropertyEditPayload(
        id: string,
        payload: Record<string, unknown>
    ): Observable<{
        property: Property;
        projects: any[];
        phases: any[];
        categories: any[];
        features: any[];
        currencies: any[];
    }> {
        return this.api.put<any>(`${this.AUTH_PATH}/${id}/save`, payload).pipe(
            map((response) => {
                const data = response?.data || response || {};
                const propertyRaw = data?.property || data?.data || data?.propertyRow;
                return {
                    property: this.mapProperty(propertyRaw || data),
                    projects: data?.projects || [],
                    phases: data?.phases || [],
                    categories: data?.categories || [],
                    features: data?.features || [],
                    currencies: data?.currencies || []
                };
            })
        );
    }

    getPropertyCreatePayload(): Observable<{
        projects: any[];
        phases: any[];
        categories: any[];
        features: any[];
        currencies: any[];
        policy?: any;
    }> {
        return this.api.get<any>(`${this.AUTH_PATH}/create-payload`).pipe(
            map((response) => {
                const data = response?.data || response || {};
                return {
                    projects: data?.projects || [],
                    phases: data?.phases || [],
                    categories: data?.categories || [],
                    features: data?.features || [],
                    currencies: data?.currencies || [],
                    policy: data?.policy || undefined
                };
            })
        );
    }

    getPropertiesByProjectAuth(projectId: string, options: { page?: number; perPage?: number } = {}): Observable<PropertyListResult> {
        const perPage = options.perPage ?? 10;
        const page = options.page ?? 1;
        let params = new HttpParams().set('project_id', projectId).set('per_page', String(perPage)).set('page', String(page));
        return this.api.get<any>(this.AUTH_PATH, params).pipe(
            map((response) => this.normalizeListResponse(response, page, perPage)),
            map(({ items, meta }) => ({
                items: items.map((item: any) => this.mapProperty(item)),
                total: meta.total,
                page: meta.page,
                perPage: meta.perPage
            }))
        );
    }

    private fetchPublic<T>(url: string, params: HttpParams): Observable<T> {
        const query = params.toString();
        const fullUrl = query ? `${url}?${query}` : url;

        return from(fetch(fullUrl, {
            cache: 'no-store',
            credentials: 'omit',
            headers: {
                'Accept': 'application/json'
            }
        })).pipe(
            switchMap(async (res) => {
                if (!res.ok) {
                    const message = await res.text().catch(() => res.statusText);
                    throw new Error(message || `Request failed with status ${res.status}`);
                }
                return res.json() as Promise<T>;
            })
        );
    }

    private normalizeListResponse(response: any, fallbackPage = 1, fallbackPerPage = 10): { items: any[]; meta: { total: number; page: number; perPage: number } } {
        const dataCandidates = [
            response?.data?.data,
            response?.data?.records,
            response?.data,
            response?.records,
            response?.meta?.data,
            response
        ];
        const items = dataCandidates.find(Array.isArray) || [];
        const metaSource = response?.meta || response?.data?.meta || response?.data?.metadata || {};
        const perPage = Number(metaSource.per_page ?? metaSource.perPage ?? fallbackPerPage);
        const page = Number(metaSource.page ?? metaSource.current_page ?? fallbackPage);
        const totalRaw = Number(metaSource.total ?? metaSource.total_records ?? metaSource.count ?? metaSource.Total ?? metaSource.totalCount);

        const safePerPage = Number.isFinite(perPage) && perPage > 0 ? perPage : fallbackPerPage;
        const safePage = Number.isFinite(page) && page > 0 ? page : fallbackPage;
        const safeTotal = Number.isFinite(totalRaw) && totalRaw >= 0
            ? totalRaw
            : (Array.isArray(items) ? items.length + (safePage - 1) * safePerPage : 0);

        return {
            items: Array.isArray(items) ? items : [],
            meta: {
                total: safeTotal,
                page: safePage,
                perPage: safePerPage
            }
        };
    }

    private normalizePaymentPlanFinancials(plan: PaymentPlan, fallbackPrice: number | null): PaymentPlan {
        const downPayment =
            typeof plan?.downPayment === 'number' && Number.isFinite(plan.downPayment) ? plan.downPayment : null;
        const reservationAmount =
            typeof plan?.reservationAmount === 'number' && Number.isFinite(plan.reservationAmount) ? plan.reservationAmount : null;
        const installmentAmount =
            typeof plan?.installmentAmount === 'number' && Number.isFinite(plan.installmentAmount) ? plan.installmentAmount : null;
        const numberOfInstallments =
            typeof plan?.numberOfInstallments === 'number' && Number.isFinite(plan.numberOfInstallments) ? plan.numberOfInstallments : null;

        // Compute the true total from actual amounts (same logic as server-side derivePaymentFromPlan).
        // Both reservation and downpayment are real payments — always include both.
        const computedTotal =
            (installmentAmount != null && numberOfInstallments != null ? installmentAmount * numberOfInstallments : 0)
            + (downPayment ?? 0)
            + (reservationAmount ?? 0);

        // Prefer the computed total; fall back to display_price or property price only when
        // the plan has no amounts defined yet (e.g., partially configured plans).
        const effectivePrice =
            computedTotal > 0
                ? computedTotal
                : typeof plan?.displayPrice === 'number' && Number.isFinite(plan.displayPrice) && plan.displayPrice > 0
                    ? plan.displayPrice
                    : typeof fallbackPrice === 'number' && Number.isFinite(fallbackPrice) && fallbackPrice > 0
                        ? fallbackPrice
                        : null;

        const downPaymentPercent =
            typeof plan?.downPaymentPercent === 'number' && Number.isFinite(plan.downPaymentPercent)
                ? plan.downPaymentPercent
                : effectivePrice && downPayment !== null
                    ? (downPayment / effectivePrice) * 100
                    : null;
        const reservationPercent =
            typeof plan?.reservationPercent === 'number' && Number.isFinite(plan.reservationPercent)
                ? plan.reservationPercent
                : effectivePrice && reservationAmount !== null
                    ? (reservationAmount / effectivePrice) * 100
                    : null;

        return {
            ...plan,
            downPaymentPercent:
                downPaymentPercent !== null ? Number(downPaymentPercent.toFixed(4)) : null,
            reservationPercent:
                reservationPercent !== null ? Number(reservationPercent.toFixed(4)) : null,
            downPayment:
                downPayment !== null
                    ? Number(downPayment.toFixed(2))
                    : effectivePrice && downPaymentPercent !== null
                        ? Number(((effectivePrice * downPaymentPercent) / 100).toFixed(2))
                        : null,
            reservationAmount:
                reservationAmount !== null
                    ? Number(reservationAmount.toFixed(2))
                    : effectivePrice && reservationPercent !== null
                        ? Number(((effectivePrice * reservationPercent) / 100).toFixed(2))
                        : null
        };
    }

    private mapPropertyVNext(data: any): Property {
        const parseNumber = (value: unknown): number | null => {
            if (typeof value === 'number' && Number.isFinite(value)) return value;
            if (typeof value === 'string') {
                const parsed = parseFloat(value);
                return Number.isFinite(parsed) ? parsed : null;
            }
            return null;
        };

        const parseInteger = (value: unknown): number | null => {
            if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
            if (typeof value === 'string') {
                const parsed = parseInt(value, 10);
                return Number.isFinite(parsed) ? parsed : null;
            }
            return null;
        };

        const parseBoolean = (value: unknown): boolean | null => {
            if (typeof value === 'boolean') return value;
            if (typeof value === 'string') {
                const normalized = value.trim().toLowerCase();
                if (['true', 'yes', '1'].includes(normalized)) return true;
                if (['false', 'no', '0'].includes(normalized)) return false;
            }
            if (typeof value === 'number') {
                if (value === 1) return true;
                if (value === 0) return false;
            }
            return null;
        };

        const country = data?.country && typeof data.country === 'object' ? data.country : null;
        const currency = data?.currency && typeof data.currency === 'object' ? data.currency : null;
        const mainImage = data?.mainImage && typeof data.mainImage === 'object' ? data.mainImage : null;
        const rawImages = Array.isArray(data?.images)
            ? data.images
            : (mainImage && (mainImage.url || mainImage.thumb))
                ? [{ ...mainImage, isMain: true, order: 0 }]
                : [];

        const images = rawImages
            .map((img: any) => {
                const url = img?.url || img?.URL || null;
                if (!url || typeof url !== 'string') return null;
                return {
                    id: img?.id || img?.ROWID || null,
                    url,
                    thumb: img?.thumb || img?.URL_THUMB || null,
                    alt: img?.alt ?? img?.ALT ?? null,
                    order: parseNumber(img?.order ?? img?.IMAGE_ORDER ?? 0),
                    isMain: Boolean(img?.isMain ?? img?.IS_MAIN ?? img?.main ?? false),
                };
            })
            .filter(Boolean) as Property['images'];

        images.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
        const mainImageUrl = images.find((img) => img.isMain)?.thumb
            || images.find((img) => img.isMain)?.url
            || images[0]?.thumb
            || images[0]?.url
            || null;

        const status = (data?.status ?? data?.STATUS ?? null) as string | null;
        const statusBadge: Property['statusBadge'] = propertyStatusKey(status);
        const price = parseNumber(data?.price ?? data?.PRICE);
        const publicDisplayPrice = parseNumber(data?.publicDisplayPrice ?? data?.public_display_price ?? data?.PUBLIC_DISPLAY_PRICE);

        const paymentPlans = Array.isArray(data?.paymentPlans)
            ? data.paymentPlans
            : Array.isArray(data?.PAYMENT_PLANS)
                ? data.PAYMENT_PLANS
                : [];

        const rawPhases = Array.isArray(data?.phases) ? data.phases : Array.isArray(data?.projectPhases) ? data.projectPhases : [];
        const mappedPhases = rawPhases
            .map((p: any) => {
                const id = p?.id || p?.ROWID || null;
                if (!id) return null;
                const name = p?.name || p?.Name || p?.NAME || null;
                return {
                    id: String(id),
                    name: name ? String(name) : 'Phase',
                    status: p?.status ?? p?.STATUS ?? null,
                    project_id: p?.project_id ?? p?.projectId ?? data?.project_id ?? data?.projectId ?? null
                };
            })
            .filter(Boolean) as NonNullable<Property['projectPhases']>;

	        const mappedPlans = paymentPlans.map((plan: any) => ({
	            id: plan?.id || plan?.ROWID || null,
	            totalPeriod: parseInteger(plan?.totalPeriod ?? plan?.TOTAL_PERIOD ?? plan?.total_period),
	            paymentCycle: parseInteger(plan?.paymentCycle ?? plan?.PAYMENT_CYCLE ?? plan?.payment_cycle),
	            downPayment: parseNumber(plan?.downPayment ?? plan?.DOWNPAYMENT ?? plan?.downpayment),
	            downPaymentPercent: parseNumber(plan?.downPaymentPercent ?? plan?.DOWNPAYMENT_PERCENT ?? plan?.downpayment_percent),
	            reservationAmount: parseNumber(plan?.reservationAmount ?? plan?.RESERVATION_AMOUNT ?? plan?.reservation_amount),
	            reservationPercent: parseNumber(plan?.reservationPercent ?? plan?.RESERVATION_PERCENT ?? plan?.reservation_percent),
	            displayPrice: parseNumber(plan?.displayPrice ?? plan?.DISPLAY_PRICE ?? plan?.display_price),
	            installmentAmount: parseNumber(plan?.installmentAmount ?? plan?.INSTALLMENT_AMOUNT ?? plan?.installment_amount),
	            numberOfInstallments: parseInteger(plan?.numberOfInstallments ?? plan?.NUMBER_OF_INSTALLMENTS ?? plan?.number_of_installments),
	            paymentTerms: plan?.paymentTerms ?? plan?.PAYMENT_TERMS ?? plan?.payment_terms ?? null,
	        })).map((plan: PaymentPlan) => this.normalizePaymentPlanFinancials(plan, publicDisplayPrice ?? price));

        const rawFeatures = Array.isArray(data?.features) ? data.features : (Array.isArray(data?.PROPERTY_FEATURES) ? data.PROPERTY_FEATURES : []);
        const mappedFeatures = rawFeatures
            .map((feature: any) => {
                const name = feature?.name || feature?.NAME || null;
                if (!name) return null;
                return {
                    id: feature?.id || feature?.ROWID || null,
                    featureId: feature?.featureId || feature?.FEATURE_ID || null,
                    name: String(name),
                    value: feature?.value ?? feature?.VALUE ?? null,
                };
            })
            .filter(Boolean) as NonNullable<Property['features']>;

        const rawLandmarks = Array.isArray(data?.landmarks) ? data.landmarks : (Array.isArray(data?.NEARBY_LANDMARKS) ? data.NEARBY_LANDMARKS : []);
        const mappedLandmarks = rawLandmarks
            .map((l: any) => {
                const name = l?.name || l?.NAME || null;
                if (!name) return null;
                return {
                    id: l?.id || l?.ROWID || null,
                    name: String(name),
                    distanceMeters: parseNumber(l?.distanceMeters ?? l?.DISTANCE_METER ?? l?.distance_meter),
                    timeMinutes: parseInteger(l?.timeMinutes ?? l?.TIME_MINUTES ?? l?.time_minutes),
                    distanceUom: (() => {
                        const raw = l?.distanceUom ?? l?.distance_uom ?? l?.DISTANCE_UOM ?? null;
                        const unit = raw !== null && raw !== undefined ? String(raw).trim() : '';
                        return unit ? unit : null;
                    })()
                };
            })
            .filter(Boolean) as NonNullable<Property['landmarks']>;

        const currencyCode = (currency?.code || data?.currency_code || data?.currencyCode || null) as string | null;
        const currencySymbol = (currency?.symbol || null) as string | null;
        const countryCode = (country?.code || data?.country_code || data?.countryCode || null) as string | null;
        const resolvedCountryCode = countryCode ? String(countryCode).trim().toUpperCase() : null;
        const countryName = (country?.name || null) as string | null;
        const resolvedCountryName = countryName || this.countryNameFromCode(resolvedCountryCode);
        const distanceKm = parseNumber(data?.distanceKm ?? data?.distance_km ?? null);
        const floorRaw = data?.floor ?? data?.FLOOR ?? null;
        const numberOfFloorsRaw = data?.numberOfFloors ?? data?.number_of_floors ?? data?.NUMBER_OF_FLOORS ?? null;
        const floor = this.normalizeUnitFloor(floorRaw) ?? (floorRaw !== null && floorRaw !== undefined ? String(floorRaw).trim() : null);
        const numberOfFloors =
            this.normalizeNumberOfFloors(numberOfFloorsRaw)
            ?? (numberOfFloorsRaw !== null && numberOfFloorsRaw !== undefined ? String(numberOfFloorsRaw).trim() : null);

        const locationRaw = data?.location ?? null;
        const locationText = locationRaw !== null && locationRaw !== undefined ? String(locationRaw).trim() : '';
        const locationUpper = locationText ? locationText.toUpperCase() : '';
        const looksLikeCountryCode = Boolean(locationUpper && /^[A-Z]{2}$/.test(locationUpper));
        const location = looksLikeCountryCode
            ? (resolvedCountryName || locationUpper || null)
            : (locationText ? locationText : null);

        const featureFlags = (() => {
            const raw = data?.feature_flags;
            if (!raw || typeof raw !== 'object') return null;
            const out: Record<string, boolean> = {};
            for (const [key, value] of Object.entries(raw)) {
                const parsed = parseBoolean(value);
                if (parsed !== null) out[key] = parsed;
            }
            return Object.keys(out).length ? out : null;
        })();
        const offerMinRequirementsMissing = (() => {
            const raw = data?.offer_min_requirements_missing;
            if (!Array.isArray(raw)) return [];
            return raw
                .map((item: any) => {
                    const key = String(item?.key || '').trim();
                    const label = String(item?.label || item?.key || '').trim();
                    if (!key || !label) return null;
                    return {
                        key,
                        label,
                        ok: item?.ok === undefined ? undefined : Boolean(item.ok),
                        details: item?.details ?? null,
                        fix_path: item?.fix_path ?? null,
                        fix_link: item?.fix_link ?? item?.fix_path ?? null
                    };
                })
                .filter((item): item is NonNullable<typeof item> => Boolean(item));
        })();

        return {
	            id: String(data?.id || data?.ROWID || ''),
	            title: String(data?.title || data?.TITLE || ''),
	            description: data?.description ?? data?.DESCRIPTION ?? null,
	            tagLine: data?.tagLine ?? data?.TAG_LINE ?? null,
	            developmentBadge: data?.developmentBadge ?? data?.development_badge ?? data?.DEVELOPMENT_BADGE ?? null,
            cardBadge1: data?.cardBadge1 ?? data?.card_badge_1 ?? data?.CARD_BADGE_1 ?? null,
            cardBadge2: data?.cardBadge2 ?? data?.card_badge_2 ?? data?.CARD_BADGE_2 ?? null,
	            locationAddress: data?.locationAddress ?? data?.location_address ?? null,
	            locationLink: data?.locationLink ?? data?.location_link ?? null,
	            price,
	            publicDisplayPrice,
            discountRate: parseNumber(data?.discountRate ?? data?.discount_rate ?? data?.DISCOUNT_RATE),
            currencyId: null,
            currency: (currencyCode || 'USD').toUpperCase(),
            currencyInfo: {
                code: currencyCode ? String(currencyCode).toUpperCase() : null,
                symbol: currencySymbol,
                name: null,
            },
            location,
            country: resolvedCountryName || (resolvedCountryCode ? resolvedCountryCode : null),
            countryName: resolvedCountryName || (resolvedCountryCode ? resolvedCountryCode : null),
            countryCode: resolvedCountryCode,
            distanceKm,
            type: data?.type ?? data?.category_name ?? data?.categoryName ?? null,
            status,
	            statusBadge,
	            bedrooms: parseInteger(data?.bedrooms ?? data?.beds ?? data?.BEDROOMS),
	            bathrooms: parseInteger(data?.bathrooms ?? data?.baths ?? data?.BATHROOMS),
	            bua: parseNumber(data?.bua ?? data?.BUA),
	            buaUnit: (() => {
	                const raw = data?.buaUnit ?? data?.bua_uom ?? data?.BUA_UOM ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            totalArea: parseNumber(data?.totalArea ?? data?.TOTAL_AREA),
	            totalAreaUnit: (() => {
	                const raw = data?.totalAreaUnit ?? data?.total_area_uom ?? data?.TOTAL_AREA_UOM ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            gardenArea: parseNumber(data?.gardenArea ?? data?.GARDEN_AREA),
	            gardenAreaUnit: (() => {
	                const raw = data?.gardenAreaUnit ?? data?.garden_area_uom ?? data?.GARDEN_AREA_UOM ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            balconyArea: parseNumber(data?.balconyArea ?? data?.BALCONY_AREA),
	            balconyAreaUnit: (() => {
	                const raw = data?.balconyAreaUnit ?? data?.balcony_area_uom ?? data?.BALCONY_AREA_UOM ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            terraceArea: parseNumber(data?.terraceArea ?? data?.TERRACE_AREA),
	            terraceAreaUnit: (() => {
	                const raw = data?.terraceAreaUnit ?? data?.terrace_area_uom ?? data?.TERRACE_AREA_UOM ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            unitNumber: data?.unitNumber ?? data?.UNIT_NUMBER ?? null,
	            floor,
	            readyToMove: parseBoolean(data?.readyToMove ?? data?.ready_to_move ?? data?.READY_TO_MOVE),
            furnishType: data?.furnishType ?? data?.furnish_type ?? data?.FURNISH_TYPE ?? null,
            finishingType: data?.finishingType ?? data?.finishing_type ?? data?.FINISHING_TYPE ?? null,
            maintenanceFeeMode: data?.maintenanceFeeMode ?? data?.maintenance_fee_mode ?? data?.MAINTENANCE_FEE_MODE ?? null,
            maintenanceFees: parseNumber(data?.maintenanceFees ?? data?.maintenance_fees ?? data?.MAINTINANCE_FREES),
            numberOfFloors,
            deliveryDate: (() => {
                const raw = data?.deliveryDate ?? data?.delivery_date ?? data?.DELIVERY_DATE ?? null;
                const label = raw !== null && raw !== undefined ? String(raw).trim() : '';
                return label ? label : null;
            })(),
            images,
            mainImage: mainImageUrl,
            features: mappedFeatures,
            featureValues: mappedFeatures,
            paymentPlans: mappedPlans,
            project_id: data?.project_id ?? data?.projectId ?? null,
            projectName: data?.projectName ?? null,
            phaseId: data?.phaseId ?? null,
            phaseName: data?.phaseName ?? null,
            projectPhases: mappedPhases,
            brochureUrl: data?.brochureUrl ?? data?.BROCHURE_URL ?? data?.brochure_url ?? null,
            brochureFileId: data?.brochureFileId ?? data?.BROCHURE_FILE_ID ?? data?.brochure_file_id ?? null,
            masterPlanUrl: data?.masterPlanUrl ?? data?.MASTER_PLAN_URL ?? data?.master_plan_url ?? null,
            masterPlanFileId: data?.masterPlanFileId ?? data?.MASTER_PLAN_FILE_ID ?? data?.master_plan_file_id ?? null,
            offerUrl: data?.offerUrl ?? data?.OFFER_URL ?? data?.offer_url ?? null,
            offerFileId: data?.offerFileId ?? data?.OFFER_FILE_ID ?? data?.offer_file_id ?? null,
            tags: (() => {
                const raw = data?.tags ?? data?.TAGS ?? null;
                if (Array.isArray(raw)) return raw.map((t: any) => String(t).trim()).filter(Boolean);
                if (raw !== null && raw !== undefined && String(raw).trim()) {
                    return String(raw)
                        .split(',')
                        .map((t: string) => t.trim())
                        .filter((t: string) => Boolean(t));
                }
                return [];
            })(),
            landmarks: mappedLandmarks,
            vendorEmail: data?.vendorEmail ?? data?.vendor_email ?? data?.vendor?.email ?? null,
            vendorPhone: data?.vendorPhone ?? data?.vendor_phone ?? data?.vendor?.phone ?? null,
            offerMinRequirementsMissing,
            feature_flags: featureFlags,
            isApproved: null,
            isDeleted: null,
        };
    }

    private mapProperty(data: any): Property {
        const parseNumber = (value: unknown): number | null => {
            if (typeof value === 'number' && Number.isFinite(value)) return value;
            if (typeof value === 'string') {
                const parsed = parseFloat(value);
                return Number.isFinite(parsed) ? parsed : null;
            }
            return null;
        };

        const parseInteger = (value: unknown): number | null => {
            if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
            if (typeof value === 'string') {
                const parsed = parseInt(value, 10);
                return Number.isFinite(parsed) ? parsed : null;
            }
            return null;
        };

        const parseBoolean = (value: unknown): boolean | null => {
            if (typeof value === 'boolean') return value;
            if (typeof value === 'string') {
                const normalized = value.trim().toLowerCase();
                if (['true', 'yes', '1'].includes(normalized)) return true;
                if (['false', 'no', '0'].includes(normalized)) return false;
            }
            if (typeof value === 'number') {
                if (value === 1) return true;
                if (value === 0) return false;
            }
            return null;
        };

        const lookups = data?.__lookups ?? {};
        const currencyLookup = lookups.CURRENCY_ID || data.currency || data.CURRENCY_ID || {};
        const projectLookup = lookups.PROJECT_ID || data.project || data.PROJECT_ID || {};
        const phaseLookup = lookups.PHASE_ID || data.phase || data.PHASE_ID || {};
        const categoryLookup = lookups.CATEGORY_ID || data.category || data.CATEGORY_ID || {};
        const vendorLookup = lookups.PROFILE_ID || data.PROFILE_ID || {};
        const organizationLookup = lookups.ORGANIZATION_ID || data.ORGANIZATION_ID || {};
        const currencyCodeFallback =
            data?.CURRENCY_CODE
            || data?.currency_code
            || data?.currencyCode
            || null;
        const currencyId =
            (currencyLookup && typeof currencyLookup === 'object' ? (currencyLookup as any)?.ROWID : null)
            || data?.CURRENCY_ID?.ROWID
            || (typeof data?.CURRENCY_ID === 'string' ? data.CURRENCY_ID : null)
            || (typeof currencyCodeFallback === 'string' ? currencyCodeFallback : null)
            || null;
        const price = parseNumber(data?.PRICE);
        const publicDisplayPrice = parseNumber(data?.PUBLIC_DISPLAY_PRICE ?? data?.publicDisplayPrice ?? data?.public_display_price);
        const brochureFileId =
            data?.BROCHURE_FILE_ID
            || data?.brochure_file_id
            || data?.brochureFileId
            || null;
        const brochureUrl = this.appendAssetVersion(this.normalizeStratusUrl(data?.BROCHURE_URL || data?.brochureUrl || null), brochureFileId);
        const masterPlanFileId =
            data?.MASTER_PLAN_FILE_ID
            || data?.master_plan_file_id
            || data?.masterPlanFileId
            || null;
        const masterPlanUrl = this.appendAssetVersion(this.normalizeStratusUrl(data?.MASTER_PLAN_URL || data?.masterPlanUrl || null), masterPlanFileId);
        const offerFileId =
            data?.OFFER_FILE_ID
            || data?.offer_file_id
            || data?.offerFileId
            || null;
        const offerUrl = this.appendAssetVersion(this.normalizeStratusUrl(data?.OFFER_URL || data?.offerUrl || null), offerFileId);
        const offerDefaultPaymentPlanId =
            data?.OFFER_DEFAULT_PAYMENT_PLAN_ID
            || data?.offer_default_payment_plan_id
            || data?.offerDefaultPaymentPlanId
            || null;
        const offerDefaultCustomPaymentJson =
            data?.OFFER_DEFAULT_CUSTOM_PAYMENT_JSON
            || data?.offer_default_custom_payment_json
            || data?.offerDefaultCustomPaymentJson
            || null;
        const offerDefaultNotes =
            data?.OFFER_DEFAULT_NOTES
            || data?.offer_default_notes
            || data?.offerDefaultNotes
            || null;
        const floorPlanFileId =
            data?.FLOOR_PLAN_FILE_ID
            || data?.floor_plan_file_id
            || data?.floorPlanFileId
            || null;
        const floorPlanUrl = this.appendAssetVersion(this.normalizeStratusUrl(data?.FLOOR_PLAN_URL || data?.floorPlanUrl || null), floorPlanFileId);
        const layoutImageFileId =
            data?.LAYOUT_IMAGE_FILE_ID
            || data?.layout_image_file_id
            || data?.layoutImageFileId
            || null;
        const layoutImageUrl = this.appendAssetVersion(this.normalizeStratusUrl(data?.LAYOUT_IMAGE_URL || data?.layoutImageUrl || null), layoutImageFileId);
        const offerHighlightFileId =
            data?.OFFER_HIGHLIGHT_FILE_ID
            || data?.offer_highlight_file_id
            || data?.offerHighlightFileId
            || null;
        const offerHighlightUrl = this.appendAssetVersion(this.normalizeStratusUrl(data?.OFFER_HIGHLIGHT_URL || data?.offerHighlightUrl || null), offerHighlightFileId);
        const isApproved = parseBoolean(data?.IS_APPROVED ?? data?.is_approved);
        const isVerified = parseBoolean(data?.IS_VERIFIED ?? data?.is_verified);
        const isPublic = parseBoolean(data?.IS_PUBLIC ?? data?.is_public);
        const locationAddress = data?.LOCATION_ADDRESS ?? data?.location_address ?? data?.locationAddress ?? null;
        const locationLink = data?.LOCATION_LINK ?? data?.location_link ?? data?.locationLink ?? null;
        const assignedToName = data?.ASSIGNED_TO ?? data?.assigned_to ?? data?.assignedToName ?? null;
        const assignedCompanyName = data?.ASSIGNED_COMPANY ?? data?.assigned_company ?? data?.assignedCompanyName ?? null;
        const assignedCountRaw = data?.ASSIGNED_COUNT ?? data?.assigned_count ?? data?.assignedCount ?? null;
        const assignedCount = Number.isFinite(Number(assignedCountRaw)) ? Math.max(0, Number(assignedCountRaw)) : 0;

        const rawTags = data?.TAGS || data?.tags;
        const tags = rawTags
            ? String(rawTags)
                .split(',')
                .map((t: string) => t.trim())
                .filter((t: string) => Boolean(t))
            : [];

        const rawImages = (data?.IMAGES || data?.images || []) as Array<any>;
        const isPending = (url: string | null | undefined) => url ? url.includes('pending.local') : false;

        const images = rawImages
            .map((image) => {
                const id = image?.ROWID || image?.row_id || image?.id || null;
                const fileId = image?.FILE_ID || image?.file_id || null;
                const versionToken = fileId || id || data?.MODIFIEDTIME || data?.updated_at || null;
                const url = this.appendAssetVersion(
                    this.normalizeStratusUrl(image?.URL || image?.IMAGE || image?.url || image?.image || null),
                    versionToken
                );
                if (!url || isPending(url)) return null;

                const isMainSource = image?.IS_MAIN ?? image?.isMain ?? image?.main;
                const isMain = typeof isMainSource === 'string'
                    ? ['true', '1', 'yes'].includes(isMainSource.toLowerCase())
                    : Boolean(isMainSource);
                const order = parseNumber(image?.IMAGE_ORDER ?? image?.SORT_ORDER ?? image?.sort_order ?? image?.DISPLAY_ORDER ?? image?.order ?? image?.Order ?? image?.orderIndex ?? image?.IMAGE_INDEX ?? image?.IMAGE_SEQ ?? image?.sequence ?? null)
                    ?? parseNumber(image?.ALT);

                return {
                    id,
                    url,
                    thumb: this.appendAssetVersion(this.normalizeStratusUrl(image?.URL_THUMB || image?.thumb || null), versionToken),
                    alt: image?.ALT || image?.Description || image?.description || null,
                    order,
                    isMain
                };
            })
            .filter(Boolean) as Property['images'];
        images.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
        const mainImage = images.find(img => img?.isMain)?.url
            || images.slice().sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))[0]?.url
            || images[0]?.url
            || null;

        const status = data?.EDITABLE_STATUS ?? data?.editable_status ?? data?.STATUS ?? data?.status ?? null;
        const statusBadge: Property['statusBadge'] = propertyStatusKey(status);

	        const paymentPlans = (data?.PAYMENT_PLANS || data?.payment_plans || data?.paymentPlans || []).map((plan: any) => ({
	            id: plan?.ROWID || plan?.row_id || plan?.id || null,
	            totalPeriod: parseInteger(plan?.TOTAL_PERIOD ?? plan?.totalPeriod ?? plan?.total_period),
	            paymentCycle: parseInteger(plan?.PAYMENT_CYCLE ?? plan?.paymentCycle ?? plan?.payment_cycle),
	            downPaymentPercent: parseNumber(plan?.DOWNPAYMENT_PERCENT ?? plan?.downPaymentPercent ?? plan?.downpayment_percent),
	            reservationPercent: parseNumber(plan?.RESERVATION_PERCENT ?? plan?.reservationPercent ?? plan?.reservation_percent),
	            downPayment: parseNumber(plan?.DOWNPAYMENT ?? plan?.downPayment ?? plan?.downpayment),
		            reservationAmount: parseNumber(plan?.RESERVATION_AMOUNT ?? plan?.reservationAmount ?? plan?.reservation_amount),
		            displayPrice: parseNumber(plan?.DISPLAY_PRICE ?? plan?.displayPrice ?? plan?.display_price),
	            installmentAmount: parseNumber(plan?.INSTALLMENT_AMOUNT ?? plan?.installmentAmount ?? plan?.installment_amount),
	            numberOfInstallments: parseInteger(plan?.NUMBER_OF_INSTALLMENTS ?? plan?.numberOfInstallments ?? plan?.number_of_installments),
	            paymentTerms: plan?.PAYMENT_TERMS ?? plan?.payment_terms ?? plan?.paymentTerms ?? null
	        })).map((plan: PaymentPlan) => this.normalizePaymentPlanFinancials(plan, publicDisplayPrice ?? price));

        const features = (data?.PROPERTY_FEATURES || []).map((feature: any, index: number) => {
            const lookup = feature?.__lookups?.FEATURE_ID || feature?.FEATURE || feature?.feature || {};
            const name = lookup?.NAME || lookup?.name || feature?.NAME || `Feature ${index + 1}`;
            const rawValue = feature?.VALUE ?? feature?.property_feature?.STRING ?? feature?.property_feature?.DECIMALS ?? feature?.property_feature?.BOOL;
            let value: string | null = null;
            if (typeof rawValue === 'boolean') {
                value = rawValue ? 'Yes' : 'No';
            } else if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                value = String(rawValue);
            }
            return name ? {
                id: feature?.ROWID || feature?.row_id || feature?.id || null,
                featureId: lookup?.ROWID || lookup?.row_id || lookup?.id || feature?.FEATURE_ID || feature?.feature_id || null,
                name,
                value
            } : null;
        }).filter(Boolean) as NonNullable<Property['features']>;

        const landmarks = (data?.NEARBY_LANDMARKS || []).map((landmark: any) => {
            if (!landmark?.NAME) return null;
            return {
                id: landmark?.ROWID || landmark?.row_id || landmark?.id || null,
                name: landmark.NAME,
                distanceMeters: parseNumber(landmark?.DISTANCE_METER),
                timeMinutes: parseInteger(landmark?.TIME_MINUTES ?? landmark?.time_minutes ?? landmark?.timeMinutes),
                distanceUom: (() => {
                    const raw = landmark?.DISTANCE_UOM ?? landmark?.distance_uom ?? landmark?.distanceUom ?? null;
                    const unit = raw !== null && raw !== undefined ? String(raw).trim() : '';
                    return unit ? unit : null;
                })()
            };
        }).filter(Boolean) as NonNullable<Property['landmarks']>;

        const rawCountry = data?.COUNTRY ?? data?.country ?? data?.address?.COUNTRY ?? null;
        const countryCode = typeof rawCountry === 'string' ? rawCountry.trim().toUpperCase() : null;
        const countryName = this.countryNameFromCode(countryCode);
        const phaseId = data?.PHASE_ID?.ROWID || data?.PHASE_ID || data?.phase_id || null;
        const phaseLookupId = phaseLookup?.ROWID || phaseLookup?.row_id || phaseLookup?.id || null;
        const normalizedPhaseId = phaseLookupId || (typeof phaseId === 'string' ? phaseId : null);
        const phaseName = phaseLookup?.Name || phaseLookup?.NAME || phaseLookup?.name || data?.PHASE_ID?.Name || data?.PHASE_ID?.NAME || null;
        const phaseStatus = phaseLookup?.STATUS || data?.PHASE_ID?.STATUS || null;
        const isDeleted = parseBoolean(data?.IS_DELETED ?? data?.is_deleted);
        const unitNumber = data?.UNIT_NUMBER || data?.unitNumber || null;
        const rawTitle = data?.TITLE ?? data?.title ?? '';
        const unitText = unitNumber !== null && unitNumber !== undefined ? String(unitNumber).trim() : '';
        const title = (() => {
            const normalized = String(rawTitle || '').trim();
            if (normalized) return normalized;
            return unitText ? `Unit ${unitText}` : '';
        })();

        const floorRaw = data?.FLOOR ?? data?.floor ?? null;
        const numberOfFloorsRaw = data?.NUMBER_OF_FLOORS ?? data?.number_of_floors ?? data?.numberOfFloors ?? null;
        const floor = this.normalizeUnitFloor(floorRaw) ?? (floorRaw !== null && floorRaw !== undefined ? String(floorRaw).trim() : null);
        const numberOfFloors = this.normalizeNumberOfFloors(numberOfFloorsRaw) ?? (numberOfFloorsRaw !== null && numberOfFloorsRaw !== undefined ? String(numberOfFloorsRaw).trim() : null);
        const featureFlags = (() => {
            const raw = data?.feature_flags;
            if (!raw || typeof raw !== 'object') return null;
            const out: Record<string, boolean> = {};
            for (const [key, value] of Object.entries(raw)) {
                const parsed = parseBoolean(value);
                if (parsed !== null) out[key] = parsed;
            }
            return Object.keys(out).length ? out : null;
        })();
        const offerMinRequirementsMissing = (() => {
            const raw = data?.offer_min_requirements_missing;
            if (!Array.isArray(raw)) return [];
            return raw
                .map((item: any) => {
                    const key = String(item?.key || '').trim();
                    const label = String(item?.label || item?.key || '').trim();
                    if (!key || !label) return null;
                    return {
                        key,
                        label,
                        ok: item?.ok === undefined ? undefined : Boolean(item.ok),
                        details: item?.details ?? null,
                        fix_path: item?.fix_path ?? null,
                        fix_link: item?.fix_link ?? item?.fix_path ?? null
                    };
                })
                .filter((item): item is NonNullable<typeof item> => Boolean(item));
        })();

        return {
	            id: String(data?.ROWID ?? data?.id ?? ''),
	            title,
	            description: data?.DESCRIPTION || data?.BODY || '',
	            tagLine: data?.TAG_LINE || null,
	            developmentBadge: data?.DEVELOPMENT_BADGE ?? data?.development_badge ?? null,
            cardBadge1: data?.CARD_BADGE_1 ?? data?.card_badge_1 ?? data?.cardBadge1 ?? null,
            cardBadge2: data?.CARD_BADGE_2 ?? data?.card_badge_2 ?? data?.cardBadge2 ?? null,
	            locationAddress: locationAddress ? String(locationAddress).trim() : null,
	            locationLink: locationLink ? String(locationLink).trim() : null,
		            price,
	            publicDisplayPrice,
            discountRate: parseNumber(data?.DISCOUNT_RATE),
            currencyId: currencyId ? String(currencyId).trim().toUpperCase() : null,
            currency: (() => {
                if (typeof currencyLookup === 'string') return currencyLookup.trim().toUpperCase() || 'USD';
                const fromLookup = currencyLookup?.CODE || currencyLookup?.code;
                if (typeof fromLookup === 'string' && fromLookup.trim()) return fromLookup.trim().toUpperCase();
                if (typeof currencyCodeFallback === 'string' && currencyCodeFallback.trim()) return currencyCodeFallback.trim().toUpperCase();
                if (typeof currencyId === 'string' && /^[A-Z]{3}$/.test(currencyId.trim().toUpperCase())) return currencyId.trim().toUpperCase();
                return 'USD';
            })(),
            currencyInfo: {
                code: (() => {
                    if (typeof currencyLookup === 'string') return currencyLookup.trim().toUpperCase() || null;
                    const fromLookup = currencyLookup?.CODE || currencyLookup?.code;
                    if (typeof fromLookup === 'string' && fromLookup.trim()) return fromLookup.trim().toUpperCase();
                    if (typeof currencyCodeFallback === 'string' && currencyCodeFallback.trim()) return currencyCodeFallback.trim().toUpperCase();
                    if (typeof currencyId === 'string' && /^[A-Z]{3}$/.test(currencyId.trim().toUpperCase())) return currencyId.trim().toUpperCase();
                    return null;
                })(),
                symbol: (currencyLookup && typeof currencyLookup === 'object' ? (currencyLookup as any)?.SYMBOL || (currencyLookup as any)?.symbol : null) || null,
                name: (currencyLookup && typeof currencyLookup === 'object' ? (currencyLookup as any)?.NAME || (currencyLookup as any)?.name : null) || null
            },
            location: (() => {
                const raw = data?.address?.CITY || data?.address?.COUNTRY || data?.LOCATION || data?.location || null;
                const text = raw !== null && raw !== undefined ? String(raw).trim() : '';
                const upper = text ? text.toUpperCase() : '';
                if (upper && /^[A-Z]{2}$/.test(upper)) return countryName || upper;
                return text ? text : null;
            })(),
            country: countryName || (typeof rawCountry === 'string' ? String(rawCountry).trim() : null),
            countryName: countryName || (typeof rawCountry === 'string' ? String(rawCountry).trim() : null),
            countryCode: countryCode && /^[A-Z]{2}$/.test(countryCode) ? countryCode : null,
            type: categoryLookup?.NAME || categoryLookup?.name || data?.Type || null,
            status,
	            statusBadge,
	            bedrooms: parseInteger(data?.BEDROOMS),
	            bathrooms: parseInteger(data?.BATHROOMS),
	            area: parseNumber(data?.TOTAL_AREA ?? data?.BUA),
	            totalArea: parseNumber(data?.TOTAL_AREA),
	            bua: parseNumber(data?.BUA),
	            buaUnit: (() => {
	                const raw = data?.BUA_UOM ?? data?.bua_uom ?? data?.buaUnit ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            totalAreaUnit: (() => {
	                const raw = data?.TOTAL_AREA_UOM ?? data?.total_area_uom ?? data?.totalAreaUnit ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            gardenArea: parseNumber(data?.GARDEN_AREA),
	            gardenAreaUnit: (() => {
	                const raw = data?.GARDEN_AREA_UOM ?? data?.garden_area_uom ?? data?.gardenAreaUnit ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            balconyArea: parseNumber(data?.BALCONY_AREA),
	            balconyAreaUnit: (() => {
	                const raw = data?.BALCONY_AREA_UOM ?? data?.balcony_area_uom ?? data?.balconyAreaUnit ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            terraceArea: parseNumber(data?.TERRACE_AREA),
	            terraceAreaUnit: (() => {
	                const raw = data?.TERRACE_AREA_UOM ?? data?.terrace_area_uom ?? data?.terraceAreaUnit ?? null;
	                return raw !== null && raw !== undefined && String(raw).trim() ? String(raw).trim() : null;
	            })(),
	            unitNumber: unitText ? unitText : null,
	            floor,
	            readyToMove: parseBoolean(data?.READY_TO_MOVE),
            furnishType: data?.FURNISH_TYPE || null,
            finishingType: data?.FINISHING_TYPE || null,
            numberOfFloors,
            maintenanceFeeMode: data?.MAINTENANCE_FEE_MODE || null,
            maintenanceFees: parseNumber(data?.MAINTINANCE_FREES ?? data?.MAINTENANCE_FEES),
            deliveryDate: data?.DELIVERY_DATE || null,
            images,
            mainImage,
            features,
            featureValues: features,
            paymentPlans,
            project_id: data?.PROJECT_ID?.ROWID || data?.PROJECT_ID || data?.project_id || null,
            projectName: projectLookup?.NAME || projectLookup?.name || null,
            categoryId: categoryLookup?.ROWID || categoryLookup?.row_id || categoryLookup?.id || data?.CATEGORY_ID || null,
            categoryName: categoryLookup?.NAME || categoryLookup?.name || null,
            phaseId: normalizedPhaseId,
            phase: normalizedPhaseId ? { id: normalizedPhaseId, name: phaseName, status: phaseStatus || null } : null,
            phaseName,
            tags,
            created_at: data?.CREATEDTIME || null,
            updated_at: data?.MODIFIEDTIME || null,
            creatorId: data?.CREATORID || lookups.CREATORID?.ROWID || lookups.CREATORID || null,
            creatorName: lookups.CREATORID?.DISPLAY_VALUE || lookups.CREATORID?.NAME || null,
            organizationId: data?.ORGANIZATION_ID || lookups.ORGANIZATION_ID || null,
            vendorId: vendorLookup?.ROWID || vendorLookup?.row_id || vendorLookup?.id || data?.PROFILE_ID || null,
            vendorName: vendorLookup?.NAME || vendorLookup?.name || vendorLookup?.FULL_NAME || vendorLookup?.full_name || organizationLookup?.NAME || organizationLookup?.name || null,
            vendorEmail: vendorLookup?.EMAIL || vendorLookup?.email || vendorLookup?.contact_email || null,
            vendorPhone: vendorLookup?.PHONE || vendorLookup?.phone || vendorLookup?.MOBILE || vendorLookup?.mobile || vendorLookup?.contact_phone || null,
            vendorLogo: vendorLookup?.LOGO_URL || vendorLookup?.logo_url || vendorLookup?.logo || vendorLookup?.IMAGE_URL || vendorLookup?.image_url || null,
            assignedToName: assignedToName !== null && assignedToName !== undefined ? String(assignedToName).trim() || null : null,
            assignedCompanyName: assignedCompanyName !== null && assignedCompanyName !== undefined ? String(assignedCompanyName).trim() || null : null,
            assignedCount,
            brochureUrl,
            brochureFileId: brochureFileId ? String(brochureFileId).trim() : null,
            masterPlanUrl,
            masterPlanFileId: masterPlanFileId ? String(masterPlanFileId).trim() : null,
            offerUrl,
            offerFileId: offerFileId ? String(offerFileId).trim() : null,
            offerDefaultPaymentPlanId: offerDefaultPaymentPlanId ? String(offerDefaultPaymentPlanId).trim() : null,
            offerDefaultCustomPaymentJson: offerDefaultCustomPaymentJson && typeof offerDefaultCustomPaymentJson === 'object' ? offerDefaultCustomPaymentJson : null,
            offerDefaultNotes: offerDefaultNotes !== null && offerDefaultNotes !== undefined ? String(offerDefaultNotes).trim() : null,
            floorPlanUrl,
            floorPlanFileId: floorPlanFileId ? String(floorPlanFileId).trim() : null,
            layoutImageUrl,
            layoutImageFileId: layoutImageFileId ? String(layoutImageFileId).trim() : null,
            offerHighlightUrl,
            offerHighlightFileId: offerHighlightFileId ? String(offerHighlightFileId).trim() : null,
            landmarks,
            offerMinRequirementsMissing,
            feature_flags: featureFlags,
            isApproved,
            isVerified,
            isPublic,
            isDeleted
        };
    }

    private normalizeStratusUrl(value: string | null | undefined): string | null {
        const raw = String(value || '').trim();
        if (!raw) return null;
        const proxyUrl = stratusFileProxyUrl(raw);
        if (proxyUrl) return proxyUrl;
        try {
            const url = new URL(raw);
            const host = url.hostname.toLowerCase();
            const stratusSuffix = '.zohostratus.com';
            if (!host.endsWith(stratusSuffix)) return raw;

            const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
            const isDevHost =
                currentHost === 'localhost'
                || currentHost === '127.0.0.1'
                || currentHost === '0.0.0.0'
                || currentHost.startsWith('dev.');

            const base = host.slice(0, -stratusSuffix.length).replace(/-development$/i, '');
            const pathname = url.pathname || '';
            const isLegacyProdOnlyObject =
                /^\/properties\/\d+\/\d+\.[a-z0-9]+$/i.test(pathname)
                && !pathname.toLowerCase().includes('/files/');

            // Some legacy property image keys exist only on the production Stratus host even in dev.
            // Keep those URLs pointing at the base host to avoid 404s like:
            //   https://propertyimages-development.zohostratus.com/properties/<digits>/<digits>.JPG
            url.hostname = isDevHost && isLegacyProdOnlyObject
                ? `${base}${stratusSuffix}`
                : isDevHost
                    ? `${base}-development${stratusSuffix}`
                    : `${base}${stratusSuffix}`;
            return url.toString();
        } catch {
            return raw;
        }
    }

    private appendAssetVersion(value: string | null | undefined, versionRaw: unknown): string | null {
        const url = String(value || '').trim();
        const version = String(versionRaw || '').trim();
        if (!url || !version) return url || null;
        try {
            const isAbsolute = /^[a-z]+:\/\//i.test(url);
            const base = typeof window !== 'undefined' ? window.location.origin : 'https://app.nibni.com';
            const parsed = new URL(url, base);
            parsed.searchParams.set('v', version);
            if (isAbsolute) return parsed.toString();
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        } catch {
            return url;
        }
    }

    private countryNameFromCode(code: string | null): string | null {
        if (!code) return null;
        const normalized = String(code).trim();
        if (!normalized) return null;
        const upper = normalized.toUpperCase();
        if (!/^[A-Z]{2}$/.test(upper)) return normalized;
        if (upper === 'EG') return 'Egypt';
        try {
            const display = new (Intl as any).DisplayNames(['en'], { type: 'region' });
            return display.of(upper) || upper;
        } catch {
            return upper;
        }
    }

    private normalizeUnitFloor(value: unknown): string | null {
        if (value === null || value === undefined) return null;
        const raw = String(value).trim();
        if (!raw) return null;
        const lower = raw.toLowerCase();

        if (lower === '0' || lower === '0.0' || lower === 'gf' || lower === 'g' || lower.includes('ground')) {
            return 'Ground';
        }
        if (lower.includes('penthouse')) return 'Penthouse';
        if (lower.includes('basement')) return 'Basement';

        const numericMatch = lower.match(/^(?:floor\\s*)?(\\d+)(?:st|nd|rd|th)?(?:\\s*floor)?$/);
        if (numericMatch?.[1]) {
            const n = parseInt(numericMatch[1], 10);
            if (Number.isFinite(n)) return String(n);
        }

        const wordMatch = lower.match(/^(?:the\\s+)?(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth)(?:\\s+floor)?$/);
        if (wordMatch?.[1]) {
            const map: Record<string, number> = {
                first: 1,
                second: 2,
                third: 3,
                fourth: 4,
                fifth: 5,
                sixth: 6,
                seventh: 7,
                eighth: 8,
                ninth: 9,
                tenth: 10,
                eleventh: 11,
                twelfth: 12,
                thirteenth: 13,
                fourteenth: 14,
                fifteenth: 15,
                sixteenth: 16,
                seventeenth: 17,
                eighteenth: 18,
                nineteenth: 19,
                twentieth: 20
            };
            const n = map[wordMatch[1]];
            if (Number.isFinite(n)) return String(n);
        }

        return raw;
    }

    private normalizeNumberOfFloors(value: unknown): string | null {
        if (value === null || value === undefined) return null;
        const raw = String(value).trim();
        if (!raw) return null;
        const lower = raw.toLowerCase().replace(/\s+/g, ' ');

        const groundPlusMatch = lower.match(/^(?:g|ground)\s*\+\s*(\d+)$/);
        if (groundPlusMatch?.[1]) {
            const n = parseInt(groundPlusMatch[1], 10);
            if (Number.isFinite(n)) return `Ground + ${n}`;
        }

        const numericMatch = lower.match(/^(\d+)\+?$/);
        if (numericMatch?.[1]) {
            const n = parseInt(numericMatch[1], 10);
            if (Number.isFinite(n)) return String(n);
        }

        if (lower === 'gf' || lower === 'ground') return 'Ground';

        return raw;
    }

    createProperty(payload: Record<string, unknown>): Observable<Property> {
        return this.api.post<any>(this.AUTH_PATH, payload).pipe(
            map((response) => this.mapProperty(response?.data ?? response))
        );
    }

    updateProperty(id: string, payload: Record<string, unknown>): Observable<Property> {
        return this.api.put<any>(`${this.AUTH_PATH}/${id}`, payload).pipe(
            map((response) => this.mapProperty(response?.data ?? response))
        );
    }

    updateOfferDefaults(
        id: string,
        payload: { offer_default_payment_plan_id?: string | null; offer_default_custom_payment_json?: any | null; offer_default_notes?: string | null }
    ): Observable<Property> {
        const propertyId = encodeURIComponent(String(id || '').trim());
        if (!propertyId) throw new Error('Missing property id');
        return this.api.patch<any>(`${this.AUTH_PATH}/${propertyId}/offer-defaults`, payload).pipe(
            map((response) => this.mapProperty(response?.data ?? response))
        );
    }

    updatePropertyStatus(id: string, status: string): Observable<{ id: string; status: string; updatedAt: string | null }> {
        const propertyId = String(id || '').trim();
        const nextStatus = String(status || '').trim();
        if (!propertyId) throw new Error('Missing property id');
        if (!nextStatus) throw new Error('Missing status');
        return this.api.patch<any>(`${this.AUTH_PATH}/${encodeURIComponent(propertyId)}/status`, { status: nextStatus }).pipe(
            map((response) => response?.data || response || {}),
            map((row: any) => ({
                id: String(row?.ROWID || row?.id || propertyId),
                status: String(row?.STATUS || row?.status || nextStatus),
                updatedAt: row?.MODIFIEDTIME || row?.updated_at || null
            }))
        );
    }

    duplicateProperty(id: string): Observable<Property> {
        return this.api.post<any>(`${this.AUTH_PATH}/${encodeURIComponent(id)}/duplicate`, {}).pipe(
            map((response) => this.mapProperty(response?.data ?? response))
        );
    }

    softDeleteProperty(id: string): Observable<Property> {
        return this.updateProperty(id, { is_deleted: true });
    }

    deleteProperty(id: string): Observable<void> {
        return this.api.delete<void>(`${this.AUTH_PATH}/${id}`);
    }

    getPropertiesByProject(projectId: string): Observable<Property[]> {
        const params = new HttpParams().set('project_id', projectId);
        return this.api.get<any>(this.AUTH_PATH, params).pipe(
            map((resp: any) => {
                const data = resp?.data || resp?.records || resp || [];
                return Array.isArray(data) ? data.map((p: any) => this.mapProperty(p)) : [];
            })
        );
    }
}
